import type { DistanceUnit$options } from '$houdini';
import type { PartnerCardsContent } from '$lib/components/advertising/partners/card/partnerCardsContent';
import type { BlablacarLocale, PartnerCardStaticContent } from '$lib/components/advertising/partners/card/types';
import type { RouteWithGeometry } from '$lib/components/map/types';
import type { Locale } from '$lib/i18n/types';
import type { SpecCardTypeType } from '../routeSpecCard/types';
import { distanceUnitLabel } from '$lib/components/constants';
import { deviceType } from '$lib/services/mediaQuery/screenSize';
import { convertDistanceUnitsToMeters } from '$lib/utils/distance';
import { formatToPartsPrice } from '$lib/utils/formatPrice';
import { getDurationHoursAndMinutes } from '$lib/utils/getDurationHoursAndMinutes';
import * as m from '$paraglide/messages';
import { languageTag } from '$paraglide/runtime';
import { get } from 'svelte/store';

export function mapRoutesWithGeometryToRouteSelector(routes: Partial<RouteWithGeometry>[]) {
	return routes.map((route, index) => {
		const { hours, minutes } = getDurationHoursAndMinutes(route.duration);
		const { hours: trafficImpactHours, minutes: trafficImpactMinutes } = getDurationHoursAndMinutes(route.trafficImpactDuration ?? 0);

		let costParts;

		if (route?.totalCost) {
			costParts = formatToPartsPrice({
				amount: route.totalCost.amount,
				currency: route.totalCost.currency,
				language: languageTag(),
				digits: 1,
			}).filter(part => part.value.trim());
		}

		return {
			duration: { hours, minutes, datetime: `${hours}:${minutes}` },
			trafficImpactDuration: trafficImpactHours > 0 || trafficImpactMinutes > 0 ? { hours: trafficImpactHours, minutes: trafficImpactMinutes, datetime: `${trafficImpactHours}:${trafficImpactMinutes}` } : undefined,
			cost: costParts,
			distance: {
				value: route?.routeDistance?.value,
				unit: distanceUnitLabel[route?.routeDistance?.unit as DistanceUnit$options],
			},
			emissions: route?.carbonDioxideEmission,
			consumption: Math.ceil(route?.electricityConsumption ?? 0),
			isDiscovery: route?.types?.includes('DISCOVERY'),
			index,
			isEv: !!route?.isEv,
		};
	});
}

export const mapRouteCardTypeToSegment: Record<SpecCardTypeType, string> = {
	cost: 'costs',
	blablacar: 'blablacar',
	roadsheet: 'instructions',
	alert: 'alert',
};

function getPrice(amount: number, currency: string, digits = 0) {
	return formatToPartsPrice({
		amount,
		currency,
		language: languageTag(),
		digits,
	}).filter(part => part.value.trim()).map(part => part.type === 'currency' ? ` ${part.value} ` : part.value).join('');
}

export function getDetailedCosts(route?: RouteWithGeometry) {
	if (!route) {
		return;
	}

	const { isEv, costs: { tolls, fuel, electricity } } = route;
	const energyCost = isEv ? electricity : fuel;
	const energyLabel = isEv ? m.route_spec_card__detailed_costs__ev_charging() : m.route_spec_card__detailed_costs__fuel();

	return {
		title: m.route_spec_card__detailed_costs__title(),
		definitions: [
			{ label: m.route_spec_card__detailed_costs__tolls(), value: getPrice(tolls.amount, tolls.currency, 1) },
			{
				label: energyLabel,
				value: getPrice(energyCost.amount, energyCost.currency, 1),
			},
		],
		buttonLabel: m.route_spec_card__detailed_costs__button_label(),
	};
}

export function getDetailedBlablacar(locale: Locale, pageName: string, departureName: string, arrivalName: string, route?: RouteWithGeometry) {
	if (!route || !shouldDisplayBlablacar(pageName, convertDistanceUnitsToMeters(route?.routeDistance), locale)) {
		return;
	}

	const device = ['desktop', 'tablet'].includes(get(deviceType)) ? 'DESKTOP' : 'MOBILE';

	return {
		title: m.route_spec_card__blablacar__title(),
		subtitle: m.route_spec_card__blablacar__subtitle(),
		srcset: 'https://bff.viamichelin.com/static/img/advertising/blablacar/card/brand-image_w160.webp w160, https://bff.viamichelin.com/static/img/advertising/blablacar/card/brand-image_w320.webp w320, https://bff.viamichelin.com/static/img/advertising/blablacar/card/brand-image_w640.webp w640, https://bff.viamichelin.com/static/img/advertising/blablacar/card/brand-image_w960.webp w960, https://bff.viamicheling.com/static/img/advertising/blablacar/card/brand-image_w1280.webp w1280, https://bff.viamichelin.com/static/img/advertising/blablacar/card/brand-image_w1920.webp w1920',
		src: 'https://bff.viamichelin.com/static/img/advertising/blablacar/card/brand-image_w1920.webp',
		description: m.route_spec_card__blablacar__description(),
		buttonLabel: m.route_spec_card__blablacar__cta(),
		alt: `${departureName} - ${arrivalName}`,
		href: m.routeresults__blablacar_card__cta_url({ arrival: arrivalName, departure: departureName, device }),
	};
}

export function getDetailedRoadsheet(route?: RouteWithGeometry) {
	if (!route) {
		return;
	}

	const { isEv, highwayDistance, carbonDioxideEmission, evData, scenicDistance } = route;

	const scenicDefinition = scenicDistance ? { label: m.roadsheet__scenic_roads(), value: `${scenicDistance.value} ${distanceUnitLabel[scenicDistance.unit as DistanceUnit$options]}` } : undefined;
	const highwayDefinition = { label: m.route_spec_card__roadsheet__highway(), value: `${highwayDistance.value} ${distanceUnitLabel[highwayDistance.unit as DistanceUnit$options]}` };

	return {
		title: m.route_spec_card__roadsheet__title(),
		definitions: [
			scenicDefinition ?? highwayDefinition,
			{
				label: isEv ? m.route_spec_card__roadsheet__ev_charging() : m.route_spec_card__roadsheet__emissions(),
				value: isEv ? `${evData?.rechargePoints.length ?? 0}` : `${carbonDioxideEmission}`,
			},
		],
		buttonLabel: m.route_spec_card__roadsheet__button_label(),
	};
}

export function getDetailedAlert(route?: RouteWithGeometry) {
	if (!route?.trafficDeviation) {
		return;
	}

	return {
		title: route.trafficDeviation.title ?? undefined,
		description: route.trafficDeviation.text,
	};
}

const BLABLACAR_THRESHOLDS_FOR_ROUTE = {
	'fr-FR': 80_000,
	'es-ES': 50_000,
};

const SUPPORTED_PAGE_NAMES = ['routecosts', 'itinerary.result'];

function isValidPageName(pageName: string | undefined) {
	return pageName ? SUPPORTED_PAGE_NAMES.includes(pageName) : false;
}

function isValidBlablacarLocale(locale: string): locale is BlablacarLocale {
	return ['es-ES', 'fr-FR'].includes(locale);
}

function shouldDisplayBlablacar(
	pageName: string,
	distance: number | undefined,
	locale: Locale,
) {
	if (!isValidBlablacarLocale(locale) || distance === undefined || !isValidPageName(pageName)) return false;

	const threshold = BLABLACAR_THRESHOLDS_FOR_ROUTE[locale];

	if (threshold == null) return false;

	return distance >= threshold;
}

function isBlablacar(
	data: PartnerCardStaticContent,
	pageName: string,
	distance: number | undefined,
	locale: Locale,
) {
	return (
		data.type === 'BLABLACAR' && shouldDisplayBlablacar(pageName, distance, locale)
	);
}

function isTuro(data: PartnerCardStaticContent, locale: string) {
	return data.type === 'TURO' && locale === 'fr-FR';
}

export function getPartnerCardData(partnerCardsContent: PartnerCardsContent, pageName: string, distance: number | undefined, locale: Locale): PartnerCardStaticContent[] {
	const pageData = partnerCardsContent?.[pageName as keyof typeof partnerCardsContent] ?? [];

	return pageData.filter(
		data => isBlablacar(data, pageName, distance, locale) || isTuro(data, locale),
	);
}
